/**
 * PairPage - Station Pairing (QR-Code Flow)
 *
 * Unauth Route für unregistrierte Geräte.
 * Zeigt QR-Code + Pairing-Code, pollt auf Admin-Genehmigung.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Monitor, RefreshCw, Loader2, CheckCircle, XCircle, Clock, Copy, Check } from 'lucide-react';
import { initPairing, claimPairing, ApiException } from '../api/client';
import QRCode from 'qrcode';

type PairingState = 'loading' | 'ready' | 'polling' | 'paired' | 'expired' | 'error';

// Security: Nur interne Pfade erlauben (kein Open Redirect)
function getSafeReturnTo(returnTo: string | null): string {
  if (!returnTo) return '/login';
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.toLowerCase().startsWith('/http')) {
    return '/login';
  }
  return returnTo;
}

export default function PairPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // returnTo aus URL Query Parameter
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  
  const [state, setState] = useState<PairingState>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stationType, setStationType] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const expiryCheckRef = useRef<NodeJS.Timeout | null>(null);

  // Pairing URL generieren
  const getPairingUrl = useCallback((t: string) => {
    return `${window.location.origin}/admin/pair?token=${t}`;
  }, []);

  // QR-Code generieren
  const generateQRCode = useCallback(async (url: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1e293b',
          light: '#ffffff',
        },
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('QR-Code Generierung fehlgeschlagen:', err);
    }
  }, []);

  // Pairing Session initialisieren
  const initSession = useCallback(async () => {
    setState('loading');
    setError(null);
    setToken(null);
    setQrDataUrl(null);

    // Polling stoppen falls aktiv
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    try {
      const deviceHint = navigator.userAgent;
      const result = await initPairing(deviceHint);

      setToken(result.token);
      setExpiresAt(new Date(result.expiresAt));

      // QR-Code generieren
      const url = getPairingUrl(result.token);
      await generateQRCode(url);

      setState('ready');
      startPolling(result.token);
      startExpiryCheck(new Date(result.expiresAt));
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'RATE_LIMITED') {
          setError(`Zu viele Anfragen. Bitte ${err.data.retryAfter || 60} Sekunden warten.`);
        } else {
          setError(err.data.error || 'Initialisierung fehlgeschlagen');
        }
      } else {
        setError('Verbindungsfehler');
      }
      setState('error');
    }
  }, [getPairingUrl, generateQRCode]);

  // Polling starten
  const startPolling = useCallback((t: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    const poll = async () => {
      try {
        const result = await claimPairing(t);

        if (result.status === 'paired') {
          // Polling stoppen
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          if (expiryCheckRef.current) {
            clearInterval(expiryCheckRef.current);
            expiryCheckRef.current = null;
          }

          setStationType(result.stationType || null);
          setState('paired');

          // Nach kurzer Verzögerung redirect zu returnTo
          setTimeout(() => {
            navigate(returnTo);
          }, 2000);
        }
      } catch (err) {
        if (err instanceof ApiException) {
          if (err.data.code === 'PAIRING_EXPIRED' || err.data.code === 'PAIRING_NOT_FOUND') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setState('expired');
          } else if (err.data.code === 'RATE_LIMITED') {
            // Bei Rate Limit kurz pausieren, aber weiter versuchen
            console.warn('Claim rate limited, waiting...');
          }
        }
      }
    };

    // Sofort einmal prüfen, dann alle 2 Sekunden
    poll();
    pollingRef.current = setInterval(poll, 2000);
  }, [navigate, returnTo]);

  // Expiry Check starten
  const startExpiryCheck = useCallback((expires: Date) => {
    if (expiryCheckRef.current) {
      clearInterval(expiryCheckRef.current);
    }

    expiryCheckRef.current = setInterval(() => {
      if (new Date() >= expires) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (expiryCheckRef.current) {
          clearInterval(expiryCheckRef.current);
          expiryCheckRef.current = null;
        }
        setState('expired');
      }
    }, 1000);
  }, []);

  // Beim Laden initialisieren
  useEffect(() => {
    initSession();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (expiryCheckRef.current) {
        clearInterval(expiryCheckRef.current);
      }
    };
  }, [initSession]);

  // Verbleibende Zeit berechnen
  const getRemainingTime = () => {
    if (!expiresAt) return null;
    const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Token-Kurzform (erste 8 Zeichen des Tokens) - nur zur visuellen Identifikation
  const tokenShort = token ? token.substring(0, 8).toUpperCase() : null;

  // Vollständige Pairing-URL
  const pairingUrl = token ? getPairingUrl(token) : null;

  // URL in Zwischenablage kopieren
  const copyUrlToClipboard = async () => {
    if (!pairingUrl) return;
    try {
      await navigator.clipboard.writeText(pairingUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.error('Kopieren fehlgeschlagen:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Monitor className="w-8 h-8 text-slate-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Station koppeln</h1>
          <p className="text-slate-500 mt-1">Dieses Gerät als Station registrieren</p>
        </div>

        {/* Loading */}
        {state === 'loading' && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-slate-400" />
            <p className="text-slate-500 mt-4">Pairing wird vorbereitet...</p>
          </div>
        )}

        {/* Ready / Polling - QR Code anzeigen */}
        {(state === 'ready' || state === 'polling') && qrDataUrl && (
          <div className="space-y-6">
            {/* QR Code */}
            <div className="flex justify-center">
              <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
                <img src={qrDataUrl} alt="Pairing QR-Code" className="w-48 h-48" />
              </div>
            </div>

            {/* Anleitung */}
            <div className="text-center space-y-2">
              <p className="text-slate-700">
                Scannen Sie den QR-Code mit einem Admin-Gerat
              </p>
            </div>

            {/* URL manuell oeffnen */}
            <div className="space-y-2">
              <p className="text-sm text-slate-600 text-center">
                Oder URL manuell auf dem Admin-Gerat offnen:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={pairingUrl || ''}
                  className="flex-1 px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg text-slate-700 truncate"
                />
                <button
                  onClick={copyUrlToClipboard}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors flex items-center gap-1"
                  title="In Zwischenablage kopieren"
                >
                  {urlCopied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-slate-600" />
                  )}
                </button>
              </div>
              {urlCopied && (
                <p className="text-xs text-green-600 text-center">
                  In Zwischenablage kopiert
                </p>
              )}
            </div>

            {/* Token-Kurzform (nur zur Identifikation) */}
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 mb-1">Token (Kurzform)</p>
              <p className="text-lg font-mono font-bold text-slate-700 tracking-widest">
                {tokenShort}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Nur zur Identifikation, nicht als Eingabe verwendbar
              </p>
            </div>

            {/* Timer */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Gultig noch {getRemainingTime()}</span>
            </div>

            {/* Status */}
            <div className="flex items-center justify-center gap-2 text-blue-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Warte auf Admin-Genehmigung...</span>
            </div>
          </div>
        )}

        {/* Paired - Erfolg */}
        {state === 'paired' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-green-700 mb-2">Station gekoppelt</h2>
            <p className="text-slate-600">
              {stationType === 'WSA' && 'WSA-Erfassung aktiv'}
              {stationType === 'VORARBEITER' && 'Vorarbeiter-Station aktiv'}
              {stationType === 'ADMIN' && 'Admin-Station aktiv'}
              {!stationType && 'Station erfolgreich registriert'}
            </p>
            <p className="text-sm text-slate-500 mt-4">Weiterleitung zur Anmeldung...</p>
          </div>
        )}

        {/* Expired */}
        {state === 'expired' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-orange-700 mb-2">Code abgelaufen</h2>
            <p className="text-slate-600 mb-6">Der Pairing-Code ist nicht mehr gultig.</p>
            <button
              onClick={initSession}
              className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Neuen Code generieren
            </button>
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-700 mb-2">Fehler</h2>
            <p className="text-slate-600 mb-6">{error || 'Ein Fehler ist aufgetreten.'}</p>
            <button
              onClick={initSession}
              className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Erneut versuchen
            </button>
          </div>
        )}

        {/* Zurück Link */}
        <div className="mt-8 text-center">
          <a href="/login" className="text-sm text-slate-500 hover:text-slate-700">
            Zuruck zur Anmeldung
          </a>
        </div>
      </div>
    </div>
  );
}
